/**
 * AI Recruit - Assignment Edit Popover Component
 * Modal for editing assignment with user search
 */

import React, { useState } from 'react'
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useOrganizationUsers, useUpdateResumeAssignment, useUpdateJobMatchAssignment } from '../../hooks/useAssignment'
import LoadingSpinner from '../common/LoadingSpinner'

interface AssignmentEditPopoverProps {
  isOpen: boolean
  onClose: () => void
  entityType: 'resume_upload' | 'job_match'
  entityId: string
  currentUserId: string | null
  currentUserName: string | null
  onSave: () => void
}

const AssignmentEditPopover: React.FC<AssignmentEditPopoverProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  currentUserId,
  currentUserName,
  onSave,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(currentUserId)

  // Fetch users with debounced search
  const { data: usersData, isLoading: isLoadingUsers, error } = useOrganizationUsers(searchTerm, isOpen)

  // Debug logging
  React.useEffect(() => {
    console.log('AssignmentEditPopover - Search term:', searchTerm)
    console.log('AssignmentEditPopover - Users data:', usersData)
    console.log('AssignmentEditPopover - Loading:', isLoadingUsers)
    console.log('AssignmentEditPopover - Error:', error)
  }, [searchTerm, usersData, isLoadingUsers, error])

  // Update mutations
  const updateResumeMutation = useUpdateResumeAssignment()
  const updateJobMatchMutation = useUpdateJobMatchAssignment()

  const handleSave = async () => {
    if (!selectedUserId) return

    try {
      if (entityType === 'resume_upload') {
        await updateResumeMutation.mutateAsync({
          resumeId: entityId,
          userId: selectedUserId,
        })
      } else {
        await updateJobMatchMutation.mutateAsync({
          jobMatchId: entityId,
          userId: selectedUserId,
        })
      }
      onSave()
      onClose()
    } catch (error) {
      console.error('Failed to update assignment:', error)
    }
  }

  const isLoading = updateResumeMutation.isPending || updateJobMatchMutation.isPending

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Reassign</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Search input */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Assigned To:</label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* User list */}
        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg mb-4">
          {isLoadingUsers ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="flex justify-center py-8 text-red-500">
              <p>Error loading users. Please try again.</p>
            </div>
          ) : !usersData?.users || usersData.users.length === 0 ? (
            <div className="flex justify-center py-8 text-gray-500">
              <p>No users found</p>
            </div>
          ) : (
            <>
              {/* User options */}
              {usersData.users.map((user) => (
                <div
                  key={user.id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-200 last:border-b-0 ${
                    selectedUserId === user.id ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => setSelectedUserId(user.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <input
                        type="radio"
                        checked={selectedUserId === user.id}
                        readOnly
                        className="mr-2"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{user.full_name}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                    </div>
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded ml-2">
                      {user.role}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Current assignment info */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
          <div className="text-gray-600">
            Current: <span className="font-medium">{currentUserName || 'Unassigned'}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || selectedUserId === currentUserId}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AssignmentEditPopover
